/**
 * Shared utilities for legacy node conversion
 * Helper functions used across different node type converters
 */
import { booleanToLegacyNumber as sharedBooleanToLegacyNumber } from '../../shared/fieldMappers'
import type { NodeData } from '../../shared/reactFlowTypes'
import { extractNumericId } from '../../shared/uuidUtils'

/**
 * Normalize option ID by removing node type prefix for auto_attendant and interactive_webhook
 * CRITICAL: Legacy Admin's save condition splits by '_' and expects numeric node ID as first part
 * For auto_attendant and interactive_webhook, option IDs must use format: {numericNodeId}_option-{random}
 * NOT: {nodeType}-{numericNodeId}_option-{random}
 *
 * This ensures: children[].id.split('_')[0] == nodeId (numeric)
 *
 * Examples:
 * - "auto_attendant-1768860996222_option-1768861568573_0" -> "1768860996222_option-1768861568573_0"
 * - "interactive_webhook-1768860799809_option-1768861568573_0" -> "1768860799809_option-1768861568573_0"
 * - "1768860845847_option-1768862242695_0" -> "1768860845847_option-1768862242695_0" (unchanged)
 *
 * @param optionId - Option ID that may contain node type prefix
 * @param nodeType - Node type ('auto_attendant', 'interactive_webhook', or 'call_distribution')
 * @returns Option ID without node type prefix
 */
export function normalizeOptionId(optionId: string, nodeType?: string): string {
  // Only normalize for auto_attendant and interactive_webhook (call_distribution already correct)
  if (
    (nodeType === 'auto_attendant' || nodeType === 'interactive_webhook') &&
    optionId.includes('-') &&
    optionId.includes('_option-')
  ) {
    // Extract numeric node ID and option part
    // "auto_attendant-1768860996222_option-1768861568573_0" -> "1768860996222_option-1768861568573_0"
    const match = optionId.match(/-(\d+_option-.*)/)
    if (match && match[1]) {
      return match[1]
    }
  }
  return optionId
}

/**
 * Generate a unique option ID for a child element
 * Preserves existing option IDs if they exist to maintain stability across conversions
 * @param nodeId - Optional node ID
 * @param index - Option index
 * @param existingOptionId - Optional existing option ID to preserve
 * @param nodeType - Optional node type for normalizing preserved IDs
 * @returns Generated option ID string
 */
export function generateOptionId(
  nodeId: string | undefined,
  index: number,
  existingOptionId?: string,
  nodeType?: string,
): string {
  // P2-1 Fix: Preserve existing option ID if it exists (from previous conversion)
  // This ensures option IDs remain stable across conversions
  if (existingOptionId && typeof existingOptionId === 'string') {
    // Validate it matches expected format (contains 'option-' or '_option-')
    if (
      existingOptionId.includes('_option-') ||
      existingOptionId.includes('option-')
    ) {
      // CRITICAL: Normalize option ID to remove node type prefix for auto_attendant and interactive_webhook
      // This ensures Legacy Admin's save condition passes (split('_')[0] == nodeId)
      return normalizeOptionId(existingOptionId, nodeType)
    }
  }

  // Generate new ID only if needed
  // CRITICAL: Extract numeric ID from nodeId to match Legacy's format
  // Legacy expects: {numericNodeId}_option-{random}
  // NOT: {fullNodeId}_option-{timestamp}_{index}
  //
  // Legacy code (line 6219): myId + '_option-' + Math.floor(1000000000000 + Math.random() * 9000000000000)
  // Where myId is the numeric node ID (e.g., "1768860996222")
  //
  // Admin3 receives nodeId as full node ID (e.g., "auto_attendant-1768860996222")
  // We must extract numeric ID to match Legacy's format
  const numericNodeId = nodeId ? extractNumericId(nodeId) : 'node'

  // Generate random number matching Legacy's format (13 digits, range 1000000000000-9999999999999)
  // Legacy uses: Math.floor(1000000000000 + Math.random() * 9000000000000)
  const randomNumber = Math.floor(1000000000000 + Math.random() * 9000000000000)

  // Return format matching Legacy: {numericNodeId}_option-{random}
  return `${numericNodeId}_option-${randomNumber}`
}

/**
 * Extract target node ID from option-{index} field in nodeData
 * @param nodeData - Node data object
 * @param index - Option index
 * @returns Target node ID if found, undefined otherwise
 */
export function getOptionTarget(
  nodeData: Record<string, unknown>,
  index: number,
): string | undefined {
  const optionKey = `option-${index}`
  if (nodeData[optionKey] && typeof nodeData[optionKey] === 'string') {
    return String(nodeData[optionKey])
  }
  return undefined
}

/**
 * Convert boolean to legacy number format (0 or 1)
 * Used for: minimized, press_1, auto_reply
 * @param value - Boolean value to convert
 * @returns Number 0 or 1
 */
export function booleanToLegacyNumber(value: boolean): number {
  return sharedBooleanToLegacyNumber(value)
}

/**
 * Handle minimized field conversion if present in nodeData
 * @param nodeData - Node data object
 * @param html - HTML object to update
 */
export function handleMinimizedField(
  nodeData: NodeData,
  html: Record<string, unknown>,
): void {
  if ('minimized' in nodeData && typeof nodeData.minimized === 'boolean') {
    html['minimized'] = booleanToLegacyNumber(nodeData.minimized)
  }
}

/**
 * Delete Admin3-only fields from result object
 * @param result - Result object to clean
 * @param fields - Array of field names to delete
 */
export function deleteAdmin3OnlyFields(
  result: Record<string, unknown>,
  fields: string[],
): void {
  fields.forEach((field) => {
    delete result[field]
  })
}

/**
 * Convert options array to legacy children array format
 * @param options - Array of option objects
 * @param nodeData - Node data object (for extracting option targets)
 * @param nodeId - Optional node ID (for generating option IDs)
 * @param fieldMapper - Function to map option fields to child object fields
 * @param nodeType - Optional node type for normalizing preserved option IDs
 * @returns Legacy children array with empty object as first element
 */
export function convertOptionsToChildren(
  options: Array<Record<string, unknown>>,
  nodeData: Record<string, unknown>,
  nodeId: string | undefined,
  fieldMapper: (
    option: Record<string, unknown>,
    child: Record<string, unknown>,
    index?: number,
  ) => void,
  nodeType?: string,
): Array<Record<string, unknown>> {
  const children: Array<Record<string, unknown>> = [{}]

  options.forEach((option, index) => {
    const child: Record<string, unknown> = {}

    fieldMapper(option, child, index)

    // P2-1 Fix: Preserve existing option ID if it exists in the option
    // This ensures option IDs remain stable across conversions
    if (!child['id']) {
      // Check if option has an existing ID from previous conversion
      if ('id' in option && typeof option.id === 'string') {
        // CRITICAL: Normalize option ID to remove node type prefix for auto_attendant and interactive_webhook
        // This ensures Legacy Admin's save condition passes (split('_')[0] == nodeId)
        child['id'] = normalizeOptionId(option.id, nodeType)
      } else {
        // Only generate new ID if option doesn't have one
        child['id'] = generateOptionId(nodeId, index, undefined, nodeType)
      }
    }

    // CRITICAL: Do NOT add 'target' field to children - this is Admin3-only and not part of Legacy format
    // The target information is stored in endpoints, not in children array items
    // Removing this prevents discrepancy with Legacy saved data

    children.push(child)
  })

  return children
}
